/**
 * Cell Filters Context
 *
 * Provides filter state to cell components without prop drilling.
 * Allows cells to opt-in to filter awareness as needed.
 *
 * @module CellFiltersContext
 */

import { createContext, useContext } from 'react';

import type { VulnerabilityFilters } from '@/sections/vulnerabilities/types/filter.types';

/**
 * Context for providing filter state to cell components
 */
const CellFiltersContext = createContext<VulnerabilityFilters | undefined>(
  undefined
);

/**
 * Provider component for cell filter context
 */
export const CellFiltersProvider = CellFiltersContext.Provider;

/**
 * Hook to access filter state from cell components
 *
 * @returns Current filter state or undefined if not within provider
 *
 * @example
 * ```typescript
 * const filters = useCellFilters();
 * const activeTagFilters = filters?.advancedFilters?.tags;
 * ```
 */
export const useCellFilters = () => {
  return useContext(CellFiltersContext);
};
